import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCalendar, FiPackage, FiFilter, FiChevronDown, FiX, FiCheck, FiHash } = FiIcons;

const UpcomingLaunchesTable = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const launchData = [
    {
      id: 1,
      productName: 'Eltrombopag Olamine Tablets',
      strengths: '12.5mg, 25mg, 50mg',
      innovatorBrand: 'Promacta',
      therapeuticCategory: 'Hematology',
      launchDate: '2026-02-12',
      launchType: 'Day 1 Launch',
      status: 'approved'
    },
    {
      id: 2,
      productName: 'Brivaracetam Tablets',
      strengths: '10mg, 25mg, 50mg, 75mg, 100mg',
      innovatorBrand: 'Briviact',
      therapeuticCategory: 'Neurology',
      launchDate: '2026-02-21',
      launchType: 'Day 181 Launch',
      status: 'pending'
    },
    {
      id: 3,
      productName: 'Mirabegron Tablets',
      strengths: '25mg, 50mg',
      innovatorBrand: 'Myrbetriq',
      therapeuticCategory: 'Urology',
      launchDate: '2026-02-23',
      launchType: 'FOF (Follow on filing)',
      status: 'development'
    },
    {
      id: 4,
      productName: 'Venetoclax Tablets',
      strengths: '10mg, 50mg, 100mg',
      innovatorBrand: 'Venclexta',
      therapeuticCategory: 'Oncology',
      launchDate: '2029-11-13',
      launchType: 'Day 1 for All',
      status: 'approved'
    },
    {
      id: 5,
      productName: 'Apremilast Tablets',
      strengths: '10mg, 20mg, 30mg',
      innovatorBrand: 'Otezla',
      therapeuticCategory: 'Immunology',
      launchDate: '2028-03-15',
      launchType: 'Day 1 Launch',
      status: 'development'
    }
  ];

  const launchTypes = [...new Set(launchData.map(item => item.launchType))];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredData = useMemo(() => {
    return launchData.filter(item => {
      const launchDate = new Date(item.launchDate);
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      
      const dateMatch = (!from || launchDate >= from) && (!to || launchDate <= to);
      const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(item.launchType);
      
      return dateMatch && typeMatch;
    });
  }, [dateFrom, dateTo, selectedTypes]);

  const getStatusColor = (status) => {
    const colors = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      development: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getLaunchTypeColor = (type) => {
    const colors = {
      'Day 1 Launch': 'bg-green-100 text-green-800',
      'Day 181 Launch': 'bg-orange-100 text-orange-800',
      'FOF (Follow on filing)': 'bg-blue-100 text-blue-800',
      'Day 1 for All': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const toggleType = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters Bar - Positioned to be integrated into the header space */}
      <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFilter} className="text-gray-400 text-sm" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          {/* Date From */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 uppercase font-semibold">From:</label>
            <input 
              type="date" 
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
          </div>

          {/* Date To */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 uppercase font-semibold">To:</label>
            <input 
              type="date" 
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
          </div>

          {/* Multi-select Launch Type */}
          <div className="relative" ref={dropdownRef}>
            <button 
              onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
              className="flex items-center gap-2 text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white hover:border-blue-400 transition-colors"
            >
              <span className="text-gray-700">
                {selectedTypes.length === 0 
                  ? 'All Launch Types' 
                  : `${selectedTypes.length} Selected`}
              </span>
              <SafeIcon icon={FiChevronDown} className={`text-gray-400 transition-transform ${isTypeDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {isTypeDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute z-10 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-2"
                >
                  <div className="flex items-center justify-between mb-2 px-2 pb-2 border-b border-gray-100">
                    <span className="text-xs font-bold text-gray-400 uppercase">Select Types</span>
                    {selectedTypes.length > 0 && (
                      <button 
                        onClick={() => setSelectedTypes([])}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {launchTypes.map(type => (
                      <label 
                        key={type}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <input 
                          type="checkbox"
                          checked={selectedTypes.includes(type)}
                          onChange={() => toggleType(type)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Reset Button */}
          {(dateFrom || dateTo || selectedTypes.length > 0) && (
            <button 
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setSelectedTypes([]);
              }}
              className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 font-medium px-2 py-1"
            >
              <SafeIcon icon={FiX} />
              Reset
            </button>
          )}
        </div>

        {/* Total Sum Widget */}
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-1.5 rounded-full">
          <SafeIcon icon={FiHash} className="text-blue-600 text-sm" />
          <span className="text-xs font-bold text-blue-900 uppercase tracking-wider">Total Products:</span>
          <span className="text-sm font-black text-blue-700">{filteredData.length}</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left py-4 px-6 font-semibold text-gray-900">Product Name</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">Strengths</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">Innovator Brand</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">Therapeutic Category</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">Launch Date</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">Launch Type</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((product, index) => (
                <motion.tr
                  key={product.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiPackage} className="text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">{product.productName}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-gray-700">{product.strengths}</span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="font-medium text-gray-900">{product.innovatorBrand}</span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-gray-700">{product.therapeuticCategory}</span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiCalendar} className="text-gray-400 text-sm" />
                      <span className="text-gray-700">{formatDate(product.launchDate)}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getLaunchTypeColor(product.launchType)}`}>
                      {product.launchType}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(product.status)}`}>
                      {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                    </span>
                  </td>
                </motion.tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <SafeIcon icon={FiFilter} className="text-3xl text-gray-300" />
                    <p>No launches match the selected filters.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UpcomingLaunchesTable;